import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../../services/auth/tokenStorage';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
  TextInput,
  Platform,
  RefreshControl,
  KeyboardAvoidingView,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NavigationProp } from '@react-navigation/native';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { API_URL } from '../../config';

// Import icons
import backIcon from '../../assets/icons/back.png';
import closeIcon from '../../assets/icons/close.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('FamilyPlan');

interface FamilyPlanScreenProps {
  navigation: NavigationProp<any>;
}

type MemberRole = 'owner' | 'spouse' | 'child';
type MemberStatus = 'active' | 'pending' | 'removed';

interface FamilyMember {
  id: string;
  email: string;
  displayName?: string;
  role: MemberRole;
  status: MemberStatus;
  dateOfBirth?: string;
  invitedAt?: string;
  acceptedAt?: string;
}

interface FamilyStatus {
  isInFamily: boolean;
  isOwner: boolean;
  planType?: string; // e.g., 'basic_spouse', 'plus_spouse', 'premium_spouse', 'family_bundle'
  planDisplayName?: string;
  ownerName?: string;
  ownerEmail?: string;
  role?: MemberRole;
  // New fields from backend
  memberCount?: number;
  maxAdults?: number;
  maxChildren?: number;
  availableSpouseSlots?: number;
  availableChildSlots?: number;
  isActive?: boolean;
  // Legacy/alternate field names (kept for safety)
  adultsUsed?: number;
  adultsLimit?: number;
  childrenUsed?: number;
  childrenLimit?: number;
}

// Human-readable plan name fallback
const planTypeLabel = (planType?: string): string => {
  switch (planType) {
    case 'basic_spouse':
      return 'Basic + Spouse';
    case 'plus_spouse':
      return 'Plus + Spouse';
    case 'premium_spouse':
      return 'Premium + Spouse';
    case 'family_bundle':
      return 'Family Bundle';
    default:
      return planType ? planType : 'Family Plan';
  }
};

const roleLabel = (role?: MemberRole): string => {
  switch (role) {
    case 'owner':
      return 'Owner';
    case 'spouse':
      return 'Spouse';
    case 'child':
      return 'Child';
    default:
      return '';
  }
};

const FamilyPlanScreen: React.FC<FamilyPlanScreenProps> = ({ navigation }) => {
  const [status, setStatus] = useState<FamilyStatus | null>(null);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteRole, setInviteRole] = useState<'spouse' | 'child'>('spouse');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteDob, setInviteDob] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);

  const fetchFamilyData = useCallback(async () => {
    setError(null);
    try {
      const token = await getAuthToken();
      if (!token) {
        setError('You are not signed in. Please sign in and try again.');
        return;
      }

      const statusRes = await fetch(`${API_URL}/family/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!statusRes.ok) {
        logger.error('Failed to fetch family status. Status:', statusRes.status);
        setError('Unable to load your family plan info. Please try again.');
        return;
      }

      const statusData: FamilyStatus = await statusRes.json();
      setStatus(statusData);

      if (statusData.isInFamily) {
        const membersRes = await fetch(`${API_URL}/family/members`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (membersRes.ok) {
          const membersData: FamilyMember[] = await membersRes.json();
          setMembers(Array.isArray(membersData) ? membersData : []);
        } else {
          logger.warn('Failed to fetch members. Status:', membersRes.status);
          setMembers([]);
        }
      } else {
        setMembers([]);
      }
    } catch (err) {
      logger.error('Error fetching family data:', err);
      setError('Something went wrong. Please check your connection and try again.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await fetchFamilyData();
      setIsLoading(false);
    })();
  }, [fetchFamilyData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchFamilyData();
    setIsRefreshing(false);
  };

  const resetInviteForm = () => {
    setInviteRole('spouse');
    setInviteEmail('');
    setInviteName('');
    setInviteDob(null);
    setShowDatePicker(false);
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    resetInviteForm();
  };

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.trim());
  };

  const handleSubmitInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) {
      Alert.alert('Email Required', 'Please enter an email address for the invite.');
      return;
    }
    if (!validateEmail(email)) {
      Alert.alert('Invalid Email', 'Please enter a valid email address.');
      return;
    }
    if (inviteRole === 'child' && !inviteDob) {
      Alert.alert('Date of Birth Required', 'Please provide a date of birth for the child.');
      return;
    }

    setIsSubmittingInvite(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Not Signed In', 'Please sign in and try again.');
        return;
      }

      const body: Record<string, unknown> = {
        email,
        role: inviteRole,
      };
      if (inviteName.trim()) {
        body.displayName = inviteName.trim();
      }
      if (inviteRole === 'child' && inviteDob) {
        body.dateOfBirth = inviteDob.toISOString();
      }

      const res = await fetch(`${API_URL}/family/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        Alert.alert(
          'Invitation Sent',
          `Invitation sent to ${email}! They'll receive an email to accept.`
        );
        closeInviteModal();
        await fetchFamilyData();
      } else {
        let message = 'Unable to send the invitation. Please try again.';
        try {
          const data = await res.json();
          if (data && (data.message || data.error)) {
            message = data.message || data.error;
          }
        } catch {
          // ignore parse errors
        }
        Alert.alert('Invite Failed', message);
      }
    } catch (err) {
      logger.error('Error sending invite:', err);
      Alert.alert('Error', 'Something went wrong. Please check your connection and try again.');
    } finally {
      setIsSubmittingInvite(false);
    }
  };

  const removeMember = async (member: FamilyMember) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Not Signed In', 'Please sign in and try again.');
        return;
      }

      const res = await fetch(`${API_URL}/family/members/${member.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (res.ok) {
        await fetchFamilyData();
      } else {
        let message = 'Unable to update membership. Please try again.';
        try {
          const data = await res.json();
          if (data && (data.message || data.error)) {
            message = data.message || data.error;
          }
        } catch {
          // ignore parse errors
        }
        Alert.alert('Action Failed', message);
      }
    } catch (err) {
      logger.error('Error removing member:', err);
      Alert.alert('Error', 'Something went wrong. Please check your connection and try again.');
    }
  };

  const confirmRemoveMember = (member: FamilyMember) => {
    const isPending = member.status === 'pending';
    const title = isPending ? 'Cancel Invitation?' : 'Remove Member?';
    const message = isPending
      ? `Cancel the pending invitation to ${member.email}?`
      : `Remove ${member.displayName || member.email} from your family plan? They will lose access to their subscription.`;
    Alert.alert(title, message, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: isPending ? 'Cancel Invite' : 'Remove',
        style: 'destructive',
        onPress: () => removeMember(member),
      },
    ]);
  };

  const handleLeaveFamily = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Not Signed In', 'Please sign in and try again.');
        return;
      }

      const res = await fetch(`${API_URL}/family/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (res.ok) {
        Alert.alert(
          'Left Family Plan',
          "You've left the family plan. You have 7 days to subscribe on your own before losing access to your vault."
        );
        await fetchFamilyData();
      } else {
        let message = 'Unable to leave the family plan. Please try again.';
        try {
          const data = await res.json();
          if (data && (data.message || data.error)) {
            message = data.message || data.error;
          }
        } catch {
          // ignore parse errors
        }
        Alert.alert('Action Failed', message);
      }
    } catch (err) {
      logger.error('Error leaving family:', err);
      Alert.alert('Error', 'Something went wrong. Please check your connection and try again.');
    }
  };

  const confirmLeaveFamily = () => {
    Alert.alert(
      'Leave Family Plan?',
      "If you leave, you'll have 7 days to subscribe on your own before losing access to your vault.",
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Leave',
          style: 'destructive',
          onPress: handleLeaveFamily,
        },
      ]
    );
  };

  const handleDateConfirm = (selectedDate: Date) => {
    setInviteDob(selectedDate);
    setShowDatePicker(false);
  };

  const formatDate = (d: Date): string => {
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity onPress={() => navigation.goBack()}>
        <Image source={backIcon} style={styles.headerIcon} />
      </TouchableOpacity>
      <Text style={styles.headerTitle}>Family Plan</Text>
      <View style={styles.placeholder} />
    </View>
  );

  const renderRoleBadge = (role: MemberRole) => {
    let backgroundColor = '#1C2C4F';
    if (role === 'spouse') backgroundColor = '#29BFC0';
    if (role === 'child') backgroundColor = '#FF9500';
    return (
      <View style={[styles.roleBadge, { backgroundColor }]}>
        <Text style={styles.roleBadgeText}>{roleLabel(role)}</Text>
      </View>
    );
  };

  const renderStatusBadge = (statusValue: MemberStatus) => {
    const color = statusValue === 'active' ? '#2EA043' : '#FF9500';
    const label = statusValue === 'active' ? 'Active' : statusValue === 'pending' ? 'Pending' : 'Removed';
    return <Text style={[styles.statusText, { color }]}>{label}</Text>;
  };

  // ---- Loading / error states ----
  if (isLoading) {
    return (
      <SafeAreaView style={styles.container} edges={['top', 'left', 'right']}>
        {renderHeader()}
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
        </View>
      </SafeAreaView>
    );
  }

  if (error || !status) {
    return (
      <SafeAreaView style={styles.container} edges={['top', 'left', 'right']}>
        {renderHeader()}
        <View style={styles.errorContainer}>
          <Text style={styles.errorTitle}>Oops!</Text>
          <Text style={styles.errorText}>
            {error || 'Unable to load your family plan info.'}
          </Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={async () => {
              setIsLoading(true);
              await fetchFamilyData();
              setIsLoading(false);
            }}
          >
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // ---- STATE 3: Not in family ----
  if (!status.isInFamily) {
    return (
      <SafeAreaView style={styles.container} edges={['top', 'left', 'right']}>
        {renderHeader()}
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              tintColor="#29BFC0"
            />
          }
        >
          <View style={styles.heroCard}>
            <Text style={styles.heroTitle}>Share MyFinalKey with your family</Text>
            <Text style={styles.heroSubtitle}>
              Add your spouse for just $20/year, or get the Family Bundle for $169.99
              covering your whole family.
            </Text>
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>What you get</Text>
            <View style={styles.featureRow}>
              <Text style={styles.featureCheck}>✓</Text>
              <Text style={styles.featureText}>
                Each member has their own private vault
              </Text>
            </View>
            <View style={styles.featureRow}>
              <Text style={styles.featureCheck}>✓</Text>
              <Text style={styles.featureText}>
                Shared billing under one subscription
              </Text>
            </View>
            <View style={styles.featureRow}>
              <Text style={styles.featureCheck}>✓</Text>
              <Text style={styles.featureText}>
                Unlimited children under 18 (Family Bundle)
              </Text>
            </View>
            <View style={styles.featureRow}>
              <Text style={styles.featureCheck}>✓</Text>
              <Text style={styles.featureText}>Easy invite by email</Text>
            </View>
          </View>

          <TouchableOpacity
            style={styles.primaryButton}
            onPress={() => navigation.navigate('SubscriptionManagement')}
          >
            <Text style={styles.primaryButtonText}>View Family Plans</Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ---- STATE 2: Member (not the owner) ----
  if (status.isInFamily && !status.isOwner) {
    const ownerName = status.ownerName || status.ownerEmail || 'Your plan owner';
    return (
      <SafeAreaView style={styles.container} edges={['top', 'left', 'right']}>
        {renderHeader()}
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              tintColor="#29BFC0"
            />
          }
        >
          <View style={styles.heroCard}>
            <Text style={styles.heroTitle}>
              You're part of {ownerName}'s Family Plan
            </Text>
            <View style={styles.memberBadgeRow}>
              {status.role ? renderRoleBadge(status.role) : null}
            </View>
            {status.planType || status.planDisplayName ? (
              <Text style={styles.heroSubtitle}>
                Plan: {status.planDisplayName || planTypeLabel(status.planType)}
              </Text>
            ) : null}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>About your plan</Text>
            <Text style={styles.infoText}>
              Your subscription is paid by {ownerName}. Your vault and data are
              completely private — only you can access them.
            </Text>
          </View>

          <TouchableOpacity
            style={styles.destructiveButton}
            onPress={confirmLeaveFamily}
          >
            <Text style={styles.destructiveButtonText}>Leave Family Plan</Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ---- STATE 1: Owner ----
  // Compute counts from backend fields. Backend returns: maxAdults, maxChildren,
  // availableSpouseSlots, availableChildSlots. Fall back to legacy field names.
  const adultsLimit = status.maxAdults ?? status.adultsLimit ?? 0;
  const childrenLimit = status.maxChildren ?? status.childrenLimit ?? 0;
  const adultsUsed = status.availableSpouseSlots !== undefined
    ? adultsLimit - status.availableSpouseSlots
    : status.adultsUsed ?? 0;
  const childrenUsed = status.availableChildSlots !== undefined
    ? childrenLimit - status.availableChildSlots
    : status.childrenUsed ?? 0;
  const planLabel = status.planDisplayName || planTypeLabel(status.planType);

  const adultsFull = adultsLimit > 0 && adultsUsed >= adultsLimit;
  const childrenFull = childrenLimit > 0 && childrenUsed >= childrenLimit;
  const canInviteAnyone = !(adultsFull && childrenFull);

  return (
    <SafeAreaView style={styles.container} edges={['top', 'left', 'right']}>
      {renderHeader()}
      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor="#29BFC0"
          />
        }
      >
        {/* Hero */}
        <View style={styles.heroCard}>
          <Text style={styles.heroSubtitleSmall}>Your Family Plan</Text>
          <Text style={styles.heroTitle}>{planLabel}</Text>
        </View>

        {/* Plan Details card */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Plan Details</Text>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Plan Type</Text>
            <Text style={styles.detailValue}>{planLabel}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Adults</Text>
            <Text style={styles.detailValue}>
              {adultsUsed} of {adultsLimit}
            </Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Children</Text>
            <Text style={styles.detailValue}>
              {childrenUsed} of {childrenLimit === 0 ? '0' : childrenLimit}
            </Text>
          </View>
        </View>

        {/* Invite button */}
        <TouchableOpacity
          style={[styles.primaryButton, !canInviteAnyone && styles.disabledButton]}
          onPress={() => {
            if (!canInviteAnyone) {
              Alert.alert(
                'Plan Full',
                'You have reached the maximum number of members for your plan.'
              );
              return;
            }
            setShowInviteModal(true);
          }}
        >
          <Text style={styles.primaryButtonText}>+ Invite Family Member</Text>
        </TouchableOpacity>

        {/* Members */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Members</Text>
          {members.length === 0 ? (
            <Text style={styles.emptyText}>
              No members yet. Tap "Invite Family Member" above to add your first one.
            </Text>
          ) : (
            members.map((member) => {
              const nameToShow = member.displayName || member.email;
              const isOwnerRow = member.role === 'owner';
              const canRemove = !isOwnerRow;
              return (
                <View key={member.id} style={styles.memberRow}>
                  <View style={styles.memberInfo}>
                    <Text style={styles.memberName} numberOfLines={1}>
                      {nameToShow}
                    </Text>
                    {member.displayName ? (
                      <Text style={styles.memberEmail} numberOfLines={1}>
                        {member.email}
                      </Text>
                    ) : null}
                    <View style={styles.memberBadgeRow}>
                      {renderRoleBadge(member.role)}
                      <View style={styles.statusWrapper}>
                        {renderStatusBadge(member.status)}
                      </View>
                    </View>
                  </View>
                  {canRemove ? (
                    <TouchableOpacity
                      style={styles.removeButton}
                      onPress={() => confirmRemoveMember(member)}
                    >
                      <Text style={styles.removeButtonText}>
                        {member.status === 'pending' ? 'Cancel Invite' : 'Remove'}
                      </Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              );
            })
          )}
        </View>

        {/* How it works */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>How it works</Text>
          <View style={styles.step}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepBadgeText}>1</Text>
            </View>
            <Text style={styles.stepText}>
              Invite a family member by email — spouse or child.
            </Text>
          </View>
          <View style={styles.step}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepBadgeText}>2</Text>
            </View>
            <Text style={styles.stepText}>
              They accept the invite and set up their own private vault.
            </Text>
          </View>
          <View style={styles.step}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepBadgeText}>3</Text>
            </View>
            <Text style={styles.stepText}>
              Billing stays under your subscription — their vault stays private to them.
            </Text>
          </View>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>

      {/* Invite Modal */}
      <Modal
        visible={showInviteModal}
        transparent
        animationType="slide"
        onRequestClose={closeInviteModal}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.modalOverlay}
        >
          <Pressable style={styles.modalBackdrop} onPress={closeInviteModal} />
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Invite Family Member</Text>
              <TouchableOpacity onPress={closeInviteModal}>
                <Image source={closeIcon} style={styles.modalCloseIcon} />
              </TouchableOpacity>
            </View>

            {/* Role toggle */}
            <View style={styles.roleToggleContainer}>
              <TouchableOpacity
                style={[
                  styles.roleToggleButton,
                  inviteRole === 'spouse' && styles.roleToggleButtonActive,
                  adultsFull && inviteRole !== 'spouse' && styles.disabledToggle,
                ]}
                onPress={() => {
                  if (adultsFull) {
                    Alert.alert(
                      'Adults Full',
                      'You have reached the maximum number of adults for your plan.'
                    );
                    return;
                  }
                  setInviteRole('spouse');
                }}
              >
                <Text
                  style={[
                    styles.roleToggleText,
                    inviteRole === 'spouse' && styles.roleToggleTextActive,
                  ]}
                >
                  Spouse
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.roleToggleButton,
                  inviteRole === 'child' && styles.roleToggleButtonActive,
                  childrenFull && inviteRole !== 'child' && styles.disabledToggle,
                ]}
                onPress={() => {
                  if (childrenFull) {
                    Alert.alert(
                      'Children Full',
                      'You have reached the maximum number of children for your plan.'
                    );
                    return;
                  }
                  setInviteRole('child');
                }}
              >
                <Text
                  style={[
                    styles.roleToggleText,
                    inviteRole === 'child' && styles.roleToggleTextActive,
                  ]}
                >
                  Child
                </Text>
              </TouchableOpacity>
            </View>

            {/* Email */}
            <Text style={styles.fieldLabel}>Email</Text>
            <TextInput
              style={styles.input}
              value={inviteEmail}
              onChangeText={setInviteEmail}
              placeholder="name@example.com"
              placeholderTextColor="#999"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
            />

            {/* Name */}
            <Text style={styles.fieldLabel}>Name (optional)</Text>
            <TextInput
              style={styles.input}
              value={inviteName}
              onChangeText={setInviteName}
              placeholder="Jane Doe"
              placeholderTextColor="#999"
            />

            {/* DOB for child */}
            {inviteRole === 'child' ? (
              <>
                <Text style={styles.fieldLabel}>Date of Birth</Text>
                <TouchableOpacity
                  style={styles.input}
                  onPress={() => setShowDatePicker(true)}
                >
                  <Text
                    style={[
                      styles.inputText,
                      !inviteDob && styles.inputPlaceholder,
                    ]}
                  >
                    {inviteDob ? formatDate(inviteDob) : 'Select date of birth'}
                  </Text>
                </TouchableOpacity>
                <DateTimePickerModal
                  isVisible={showDatePicker}
                  mode="date"
                  date={inviteDob || new Date()}
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  maximumDate={new Date()}
                  onConfirm={handleDateConfirm}
                  onCancel={() => setShowDatePicker(false)}
                />
              </>
            ) : null}

            {/* Submit */}
            <TouchableOpacity
              style={[
                styles.primaryButton,
                isSubmittingInvite && styles.disabledButton,
              ]}
              onPress={handleSubmitInvite}
              disabled={isSubmittingInvite}
            >
              {isSubmittingInvite ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.primaryButtonText}>Send Invitation</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={closeInviteModal}
              disabled={isSubmittingInvite}
            >
              <Text style={styles.secondaryButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#1C2C4F',
  },
  placeholder: {
    width: s(24),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(30),
  },
  errorTitle: {
    fontSize: ms(22, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  errorText: {
    fontSize: ms(15, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(20),
    lineHeight: ms(22, 0.3),
  },
  retryButton: {
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(30),
    paddingVertical: ms(12),
    borderRadius: ms(50),
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  heroCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  heroTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  heroSubtitle: {
    fontSize: ms(15, 0.3),
    color: '#666',
    lineHeight: ms(22, 0.3),
  },
  heroSubtitleSmall: {
    fontSize: ms(13, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
    marginBottom: ms(6),
    textTransform: 'uppercase',
    letterSpacing: ms(0.5),
  },
  sectionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(15),
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: ms(8),
  },
  detailLabel: {
    fontSize: ms(15, 0.3),
    color: '#666',
  },
  detailValue: {
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
  },
  infoText: {
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    lineHeight: ms(22, 0.3),
  },
  emptyText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#EEF1F4',
  },
  memberInfo: {
    flex: 1,
    marginRight: ms(10),
  },
  memberName: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(2),
  },
  memberEmail: {
    fontSize: ms(13, 0.3),
    color: '#666',
    marginBottom: ms(6),
  },
  memberBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: ms(4),
  },
  roleBadge: {
    paddingHorizontal: ms(10),
    paddingVertical: ms(4),
    borderRadius: ms(12),
    marginRight: ms(8),
  },
  roleBadgeText: {
    color: '#FFFFFF',
    fontSize: ms(12, 0.3),
    fontWeight: '600',
  },
  statusWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusText: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
  },
  removeButton: {
    paddingHorizontal: ms(12),
    paddingVertical: ms(8),
    borderRadius: ms(20),
    borderWidth: ms(1),
    borderColor: '#E85C5C',
  },
  removeButtonText: {
    color: '#E85C5C',
    fontSize: ms(13, 0.3),
    fontWeight: '600',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#29BFC0',
    borderRadius: ms(50),
    paddingVertical: ms(14),
    paddingHorizontal: ms(20),
    marginBottom: ms(20),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(50),
    paddingVertical: ms(14),
    paddingHorizontal: ms(20),
    borderWidth: ms(1.5),
    borderColor: '#29BFC0',
    marginBottom: ms(10),
  },
  secondaryButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  destructiveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(50),
    paddingVertical: ms(14),
    paddingHorizontal: ms(20),
    borderWidth: ms(1.5),
    borderColor: '#E85C5C',
    marginBottom: ms(20),
  },
  destructiveButtonText: {
    color: '#E85C5C',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  disabledButton: {
    opacity: 0.5,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: ms(10),
  },
  featureCheck: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    fontWeight: 'bold',
    marginRight: ms(10),
  },
  featureText: {
    flex: 1,
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    lineHeight: ms(22, 0.3),
  },
  step: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(15),
  },
  stepBadge: {
    width: s(32),
    height: s(32),
    borderRadius: ms(16),
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: ms(15),
  },
  stepBadgeText: {
    color: '#FFFFFF',
    fontSize: ms(15, 0.3),
    fontWeight: 'bold',
  },
  stepText: {
    flex: 1,
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    lineHeight: ms(22, 0.3),
  },
  bottomPadding: {
    height: vs(30),
  },

  // Modal styles
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    padding: ms(20),
    paddingBottom: ms(30),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  modalTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
  },
  modalCloseIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  roleToggleContainer: {
    flexDirection: 'row',
    backgroundColor: '#F3F6F8',
    borderRadius: ms(50),
    padding: ms(4),
    marginBottom: ms(16),
  },
  roleToggleButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: ms(10),
    borderRadius: ms(50),
  },
  roleToggleButtonActive: {
    backgroundColor: '#29BFC0',
  },
  roleToggleText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  roleToggleTextActive: {
    color: '#FFFFFF',
  },
  disabledToggle: {
    opacity: 0.4,
  },
  fieldLabel: {
    fontSize: ms(13, 0.3),
    color: '#666',
    fontWeight: '600',
    marginBottom: ms(6),
    marginTop: ms(6),
  },
  input: {
    backgroundColor: '#F3F6F8',
    borderRadius: ms(12),
    paddingHorizontal: ms(15),
    paddingVertical: ms(12),
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    marginBottom: ms(12),
    minHeight: ms(46),
    justifyContent: 'center',
  },
  inputText: {
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
  },
  inputPlaceholder: {
    color: '#999',
  },
});

export default FamilyPlanScreen;
